"use client"

// CSS imports
import "@glidejs/glide/dist/css/glide.core.min.css"

// React and Next.js imports
import React, { useEffect, useMemo, useRef } from "react"
import { useParams } from "next/navigation"

// Third-party library imports
import { Message } from "@locmod/intl"
import { useActiveMarket, useActiveMarkets, useGames } from "@azuro-org/sdk"
import {
  ConditionState,
  Game_OrderBy,
  type GameMarkets,
  type GamesQuery,
} from "@azuro-org/toolkit"
import cx from "classnames"
import Glide from "@glidejs/glide"
import { orderBy } from "lodash"

// Local component imports
import { Icon, type IconName } from "components/ui"
import { OpponentLogo } from "components/dataDisplay"
import { Href } from "components/navigation"
import OutcomeButton from "compositions/OutcomeButton/OutcomeButton"
import TimeValue from "compositions/TimeValue"
import { Condition as OvertimeCondition } from "@/compositions/event/Markets/OvertimeMarkets"

// Local utility and hook imports
import { getGameDateTime } from "helpers/getters"
import { useLeagueSortedMarkets } from "@/hooks/useLeagueSortedMarkets"
import { useGameInfo } from "@/app/OvertimePages/SingleGameContent"
import useChainProvider from "@/hooks/useChainProvider"
import { type SupportedNetwork } from "@/Overtime/types/network"
import { useLazyTeamImage } from "@/Overtime/utils/images"

// Local file imports
import messages from "./messages"

type ConditionProps = {
  markets: GameMarkets
  game: GamesQuery["games"][0]
  section?: string
}

const Condition: React.FC<ConditionProps> = ({ markets, game }) => {
  const { data } = useActiveMarket({ markets })

  const { marketsByKey, activeMarketKey, states } = data

  const { name, conditions } = marketsByKey[activeMarketKey!]
  const { conditionId, outcomes } = conditions[0]

  return (
    <>
      {outcomes.map(outcome => (
        <OutcomeButton
          key={outcome.outcomeId}
          marketName={name}
          outcome={outcome}
          game={game}
          isLocked={states[conditionId] !== ConditionState.Active}
        />
      ))}
    </>
  )
}

const CardSkeleton: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <div className={cx("bone h-[12.125rem] w-full rounded-md", className)} />
  )
}

type CardProps = {
  game: GamesQuery["games"][0]
}

const Card: React.FC<CardProps> = ({ game }) => {
  const {
    sport: { slug: sportSlug },
    league: { name: leagueName, slug: leagueSlug },
    country: { name: countryName, slug: countrySlug },
    gameId,
    participants,
    startsAt,
    title,
  } = game

  const { date, time } = getGameDateTime(+startsAt * 1000)

  const { data: markets, isFetching } = useActiveMarkets({
    gameId: game.gameId,
  })

  return (
    <div className="bg-card-border-bottom p-px rounded-md overflow-hidden">
      <div className="bg-gradient-event-card p-4 rounded-md">
        <Href
          to={`${sportSlug}/${countrySlug}/${leagueSlug}/${gameId}`}
          className="flex items-center justify-center text-accent-grey-200 text-caption-13 hover:underline"
        >
          <Icon
            className="size-4 mr-2 flex-none"
            name={`sport/${sportSlug}` as IconName}
          />
          <span className="text-ellipsis whitespace-nowrap overflow-hidden">
            {countryName}
          </span>
          <div className="size-[2px] rounded-full bg-accent-grey-700 mx-1" />
          <span className="text-ellipsis whitespace-nowrap overflow-hidden">
            {leagueName}
          </span>
        </Href>
        <div className="mt-3 flex items-center justify-between px-7">
          <OpponentLogo image={participants[0].image} size={48} />
          <div className="text-caption-12 text-center">
            <div className="text-accent-grey-200">{date}</div>
            <div className="font-semibold mt-[2px]">
              <TimeValue time={time} />
            </div>
          </div>
          <OpponentLogo image={participants[1].image} size={48} />
        </div>
        <div className="mt-5 text-caption-13 font-semibold text-center text-ellipsis whitespace-nowrap overflow-hidden">
          {title}
        </div>
        <div className="mt-3 flex items-center space-x-2">
          {isFetching ? (
            <>
              <div className="bone w-full h-7 rounded-sm" />
              <div className="bone w-full h-7 rounded-sm" />
              <div className="bone w-full h-7 rounded-sm" />
            </>
          ) : (
            <Condition markets={markets!} game={game} section="Hot" />
          )}
        </div>
      </div>
    </div>
  )
}

const OvertimeCard: React.FC<any> = ({ game }) => {
  const { gameInfo } = useGameInfo(game as any)
  const { sport, league, country, participants, startsAt, title } = gameInfo

  const { date, time } = getGameDateTime(+startsAt)

  // Lazy load team images for better performance
  const homeTeamImage = useLazyTeamImage(
    participants[0]?.name || "",
    game.leagueId
  )
  const awayTeamImage = useLazyTeamImage(
    participants[1]?.name || "",
    game.leagueId
  )

  const sortedChildMarkets = useMemo(() => {
    if (!game.childMarkets || game.childMarkets.length === 0) {
      return []
    }

    const marketsWithTwoOdds = game.childMarkets.filter(market => {
      const hasTwoOdds = market.odds?.length === 2
      return hasTwoOdds
    })
    return orderBy(marketsWithTwoOdds, ["line", "odds"], ["asc", "desc"])
  }, [game.childMarkets])

  return (
    <div className="bg-card-border-bottom p-px rounded-md overflow-hidden">
      <div className="bg-gradient-event-card p-4 rounded-md">
        <Href
          to={`${sport.slug}/${country.slug}/${league.slug}/${game.gameId}`}
          className="flex items-center justify-center text-accent-grey-200 text-caption-13 hover:underline"
        >
          <Icon
            className="size-4 mr-2 flex-none"
            name={`sport/${sport.slug}` as IconName}
          />
          <span className="text-ellipsis whitespace-nowrap overflow-hidden">
            {country.name}
          </span>
          <div className="size-[2px] rounded-full bg-accent-grey-700 mx-1" />
          <span className="text-ellipsis whitespace-nowrap overflow-hidden">
            {league.name}
          </span>
        </Href>
        <div className="mt-3 flex items-center justify-between px-7">
          {homeTeamImage.isLoading || !homeTeamImage.imageSrc ? (
            <div className="w-12 h-12 rounded-full bg-accent-grey-700 flex items-center justify-center text-xs font-semibold text-white" />
          ) : (
            <OpponentLogo image={homeTeamImage.imageSrc} size={48} />
          )}
          <div className="text-caption-12 text-center">
            <div className="text-accent-grey-200">{date}</div>
            <div className="font-semibold mt-[2px]">
              <TimeValue time={time} />
            </div>
          </div>
          {awayTeamImage.isLoading || !awayTeamImage.imageSrc ? (
            <div className="w-12 h-12 rounded-full bg-accent-grey-700 flex items-center justify-center text-xs font-semibold text-white" />
          ) : (
            <OpponentLogo image={awayTeamImage.imageSrc} size={48} />
          )}
        </div>
        <div className="mt-5 text-caption-13 font-semibold text-center text-ellipsis whitespace-nowrap overflow-hidden">
          {title}
        </div>
        <div className="space-y-2 rounded-sm p-2">
          {sortedChildMarkets.length > 0 ? (
            <OvertimeCondition
              key={`${game.gameId}-${game.typeId}-${game.line}-${game.playerProps?.playerId || "no-player"}`}
              condition={sortedChildMarkets[0]}
              marketName="game.name"
              market={game}
              isGameOpen
            />
          ) : (
            <div className="text-caption-12 text-center text-accent-grey-400 py-2">
              No markets available
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const sliderConfiguration = {
  gap: 8,
  perView: 3,
  startAt: 0,
  focusAt: 0,
  autoplay: 5000,
  bound: true,
  breakpoints: {
    802: {
      perView: 1.1,
    },
  },
}

const Events: React.FC = () => {
  const { isOvertimeChain, isAzuroChain, chainId } = useChainProvider()

  const { data: azuroGames, isFetching: isAzuroFetching } = useGames({
    filter: {
      limit: 9,
    },
    orderBy: Game_OrderBy.Turnover,
    query: {
      enabled: isAzuroChain,
    },
  })

  const { sortedMarkets: overtimeGames, isLoading: isMarketsLoading } =
    useLeagueSortedMarkets({
      networkId: chainId as SupportedNetwork,
      maxGames: 9,
      enabled: isOvertimeChain,
    })

  const games = (() => {
    switch (true) {
      case isOvertimeChain: {
        return overtimeGames
      }
      default: {
        return azuroGames
      }
    }
  })()

  const isFetching = (() => {
    switch (true) {
      case isOvertimeChain: {
        return isMarketsLoading
      }
      default: {
        return isAzuroFetching
      }
    }
  })()

  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!games?.length || isFetching) {
      return
    }

    const slider = new Glide(containerRef.current, sliderConfiguration)

    slider.mount()

    return () => {
      slider?.destroy()
    }
  }, [games, isFetching])

  if (isFetching) {
    return (
      <div className="flex items-center justify-between mt-6 space-x-2">
        <CardSkeleton />
        <CardSkeleton className="mb:hidden" />
        <CardSkeleton className="mb:hidden" />
      </div>
    )
  }

  return (
    <div ref={containerRef} className="glide !static group mt-6 mb-6">
      <div className="glide__track" data-glide-el="track">
        <ul className="glide__slides">
          {games?.map((game, index) => (
            <li key={index} className="glide__slide overflow-hidden">
              {isOvertimeChain && <OvertimeCard game={game} />}
              {isAzuroChain && <Card game={game} />}
            </li>
          ))}
        </ul>
      </div>
      <div
        className="absolute top-6 right-6 flex items-center"
        data-glide-el="controls"
      >
        <button
          className="w-8 h-6 flex items-center justify-center bg-accent-grey-980 rounded-tl-full rounded-tr-1 rounded-br-1 rounded-bl-full border border-accent-grey-800 text-accent-grey-200 hover:text-accent-grey-20 transition backdrop-blur-[17px] "
          data-glide-dir="<"
        >
          <Icon className="size-5" name="interface/chevron_left" />
        </button>
        <button
          className="w-8 h-6 flex items-center justify-center bg-accent-grey-980 rounded-tl-1 rounded-tr-full rounded-br-full rounded-bl-1 border border-accent-grey-800 text-accent-grey-200 hover:text-accent-grey-20 transition ml-1 backdrop-blur-[17px] "
          data-glide-dir=">"
        >
          <Icon className="size-5" name="interface/chevron_right" />
        </button>
      </div>
    </div>
  )
}

const TopEvents: React.FC = () => {
  const params = useParams()
  const sport = params?.sportSlug
    ? messages[params.sportSlug as string]
    : undefined

  return (
    <div className="relative pt-6">
      <div className="px-4">
        <Message
          className="text-caption-13 text-accent-grey-200 uppercase"
          value={messages.title}
        />
        <h1 className="text-heading-h1 font-bold">
          <Message className="text-brand-light-500" value={messages.top} />
          {Boolean(sport) && <Message className="ml-2" value={sport} />}
          <Message className="ml-2" value={messages.events} />
        </h1>
      </div>
      <Events />
    </div>
  )
}

export default TopEvents
